import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery } from "@apollo/client";
import { Box, Alert, Button } from "@mui/material";
import BookingTypeTime from "./bookingTypeTime";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import "react-color-palette/lib/css/styles.css";
import "react-phone-number-input/style.css";
import { UserContext } from "../../../../../platform/components/auth/auth-context";
import { useDispatch } from "react-redux";
import ConfirmDialog from "./confirmDialog";
import { useCloseConfirmation } from "./useCloseConfirmation";
import { DELETE_INVITEE_QUESTION } from "../../../../../platform/graphql/mutations";
import { useSelectedBusiness } from "../../../../../platform/redux/store";
import useLoadReduxStoreData from "../../../../../platform/redux/reducers/loadReduxStoreData";
import { ADD_BOOKING_TYPE, UPDATE_BOOKING_TYPE } from "../../../../../platform/graphql/mutations/bookingType";
import { updateBookingType } from "../../../../../platform/redux/actions/businessActions";
import {
    GET_BILLING_SUBSCRIPTION,
    GET_BUSINESS_BOOKING_TYPES,
    GET_PLANS,
    BusinessQueryNames,
} from "../../../../../platform/graphql/queries";
import { usePermissions } from "../../../../../platform/hooks/usePermissions";
import UpgradePlan from "../../../../../platform/util/upgrade/upgradePlan";
import {
    FeatureAccess,
    getFeatureAccess,
} from "../../../../../platform/util/featureAccess";
import {
    hasPaidBookingFeatures,
    hasStandaloneBookingAccess,
    isProductVisible,
} from "../../../../../core/business/productAccess";

/* ---------------------------------------------
 * Helpers
 * -------------------------------------------*/
const bookingCategoryMap = { event: 1, service: 2, class: 3 };
const LOCAL_WORKSPACE_PRODUCTS_KEY = "planyLocalWorkspaceProducts";
const ACTIVE_BOOKING_STATUSES = new Set(["trialing", "active", "past_due", "canceling"]);
const FREE_BOOKING_TYPE_LIMIT = 2;
const TOUR_ONLY_BOOKING_TYPE_KEYWORDS = ["tour", "visit", "walkthrough"];

function isTourOnlyConciergeBookingType(bookingType) {
    if (!bookingType?.createdByAgentId) return false;

    const title = String(bookingType?.title || "").toLowerCase();
    const link = String(bookingType?.link || "").toLowerCase();

    return TOUR_ONLY_BOOKING_TYPE_KEYWORDS.some(
        (keyword) => title.includes(keyword) || link.includes(keyword)
    );
}

function getActiveProductId(location) {
    const params = new URLSearchParams(location?.search || "");
    const fromQuery = String(params.get("v") || "").toLowerCase();
    if (fromQuery === "booking" || fromQuery === "care") return fromQuery;
    return String(location?.pathname || "").includes("/booking") ? "booking" : "care";
}

function readLocalWorkspaceProducts(businessId) {
    if (!businessId || typeof window === "undefined") return {};
    try {
        const raw = window.localStorage.getItem(LOCAL_WORKSPACE_PRODUCTS_KEY);
        if (!raw) return {};
        const parsed = JSON.parse(raw);
        return parsed && typeof parsed === "object" ? (parsed[String(businessId)] || {}) : {};
    } catch {
        return {};
    }
}

const getBookingCategory = (input) => {
    if (typeof input === "string") {
        return bookingCategoryMap[input.toLowerCase()] !== undefined ? input.toLowerCase() : "";
    } else if (typeof input === "number") {
        for (const category in bookingCategoryMap) {
            if (bookingCategoryMap[category] === input) return category;
        }
        return "";
    }
    return "";
};

/* ---------------------------------------------
 * Context
 * -------------------------------------------*/
const BookingTypeContext = createContext();
export const useBookingTypeContext = () => useContext(BookingTypeContext);

/* ---------------------------------------------
 * Provider
 * -------------------------------------------*/
function BookingTypeProvider(allProps) {
    const workspaceLoad = useLoadReduxStoreData(BusinessQueryNames.GET_MY_BUSINESSES);
    const selectedBusiness = useSelectedBusiness();
    const hasBusinessUrl = !!selectedBusiness?.businessBasic?.url;
    const businessId = Number(selectedBusiness?.businessBasic?.id || 0) || null;

    const { children, ...props } = allProps;
    const location = useLocation();
    const activeProductId = useMemo(() => getActiveProductId(location), [location]);
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const isUpdateMode = location.pathname.includes("/update/");
    const { id /*, bookingtypecategory*/ } = useParams();

    // IMPORTANT: consider "new", "create", or any non-number as "new"
    const isNewBookingType = !id || Number.isNaN(Number(id));
    const bookingTypeId = Number(id || 0);
    const localBusinessBookingTypes = useMemo(
        () => (Array.isArray(selectedBusiness?.businessBookingTypes)
            ? selectedBusiness.businessBookingTypes
            : []),
        [selectedBusiness?.businessBookingTypes]
    );
    const hasLocalBookingTypes = localBusinessBookingTypes.length > 0;
    const localBookingType = !isNewBookingType
        ? localBusinessBookingTypes.find((obj) => Number(obj?.id) === bookingTypeId) || null
        : null;
    const shouldFetchBusinessBookingTypes =
        hasBusinessUrl && (!hasLocalBookingTypes || (!isNewBookingType && !localBookingType));

    const {
        CancelConfirmationModal,
    } = useCloseConfirmation(props);

    const {
        loading,
        error,
        data: businessBookingTypes,
        refetch,
    } = useQuery(GET_BUSINESS_BOOKING_TYPES, {
        variables: { businessUrl: selectedBusiness?.businessBasic?.url },
        skip: !shouldFetchBusinessBookingTypes,
        fetchPolicy: "cache-and-network",
        nextFetchPolicy: "cache-first",
    });

    const [deleteInviteeQuestion] = useMutation(DELETE_INVITEE_QUESTION, {
        onCompleted: () => window.location.reload(),
    });

    const [isDisabled, setIsDisabled] = useState(false);
    const [errors, setErrors] = useState({});
    const [bookingType, setBookingType] = useState(null);
    const [isScheduleSettings, setIsScheduleSettings] = useState(false);
    const [showInviteeQuestions, setShowInviteeQuestions] = useState(false);
    const [showSection, setShowSection] = useState("");
    const [showBookingBasicDialogSection, setShowBookingBasicDialogSection] = useState("basic");
    const [isBookingBasicDialogOpen, setIsBookingBasicDialogOpen] = useState(!isUpdateMode);
    const [openDelete, setOpenDelete] = useState(false);
    const [currentId, setCurrentId] = useState(null);
    const [selectedTab, setSelectedTab] = useState(!isUpdateMode && 0);
    const [updateBookingTypeDB] = useMutation(UPDATE_BOOKING_TYPE);
    const [addBookingType] = useMutation(ADD_BOOKING_TYPE);
    const [additionalOptions, setAdditionalOptions] = useState(false);
    const [updating, setUpdating] = useState(false);
    const [bookingApproval, setBookingApproval] = useState(0);

    const timeoutRef = useRef(null);
    const timeoutActiveRef = useRef(false);
    const notificationRef = useRef(null);

    const userContext = useContext(UserContext);
    const providerUser = userContext?.providerUser;
    const isCareBusiness = Boolean(
        selectedBusiness?.careSettings?.afhLicenseNumber ||
        selectedBusiness?.businessBasic?.careSettings?.afhLicenseNumber
    );
    const [localWorkspaceProducts, setLocalWorkspaceProducts] = useState(() =>
        readLocalWorkspaceProducts(businessId)
    );
    useEffect(() => {
        setLocalWorkspaceProducts(readLocalWorkspaceProducts(businessId));
    }, [businessId, location.search]);
    const { data: bookingBillingData } = useQuery(GET_BILLING_SUBSCRIPTION, {
        variables: { businessId },
        skip: !businessId,
        fetchPolicy: "network-only",
    });
    const { data: plansData } = useQuery(GET_PLANS, {
        skip: !businessId,
    });
    const bookingBillingStatus = String(
        bookingBillingData?.getBillingSubscription?.status || ""
    ).toLowerCase();
    const hasBookingBillingAccess = ACTIVE_BOOKING_STATUSES.has(bookingBillingStatus);
    const isBookingContext = activeProductId === "booking";
    const hasStandaloneBooking =
        isBookingContext ||
        hasStandaloneBookingAccess(providerUser, isCareBusiness) ||
        isProductVisible(providerUser, "booking") ||
        hasBookingBillingAccess ||
        !!localWorkspaceProducts.booking;
    const hasAdvancedBookingAccess = hasPaidBookingFeatures(
        providerUser,
        bookingBillingData?.getBillingSubscription,
        plansData?.plans || [],
        isCareBusiness
    ) || hasBookingBillingAccess;
    const bookingCreateAccess = getFeatureAccess(
        providerUser,
        FeatureAccess.BOOKING_TYPE_CREATE
    );
    const standardPlanRequired = bookingCreateAccess.requiredPlanId;
    const { canEditBookingType } = usePermissions();
    const [checkPlanNeeded, setCheckPlanNeeded] = useState(false);
    const [upgradeFeatureName, setUpgradeFeatureName] = useState("Booking types");
    const existingBookingTypes = useMemo(() => {
        const source = businessBookingTypes?.getBusinessBookingTypes || localBusinessBookingTypes;
        return (Array.isArray(source) ? source : []).filter(
            (bookingType) => !isTourOnlyConciergeBookingType(bookingType)
        );
    }, [businessBookingTypes?.getBusinessBookingTypes, localBusinessBookingTypes]);

    /* ---------------------------------------------
     * Debug helpers (you can remove after verifying)
     * -------------------------------------------*/
    useEffect(() => {
        // Minimal diagnostics to see the states
        // (remove or comment out in production)
        // console.log({ hasBusinessUrl, id, isNewBookingType, loading, error, businessBookingTypes });
    }, [hasBusinessUrl, id, isNewBookingType, loading, error, businessBookingTypes]);

    useEffect(() => {
        if (isNewBookingType && !hasStandaloneBooking) {
            setUpgradeFeatureName("Booking types");
            setCheckPlanNeeded(true);
        }
    }, [isNewBookingType, hasStandaloneBooking]);

    /* ---------------------------------------------
     * Permission-based disable toggle
     * -------------------------------------------*/
    useEffect(() => {
        const createdBy = Number(bookingType?.createdBy);
        const canEdit = canEditBookingType(createdBy);
        setIsDisabled(!canEdit);
    }, [canEditBookingType, bookingType]);

    /* ---------------------------------------------
     * Updating notification bar timing
     * -------------------------------------------*/
    useEffect(() => {
        if (updating && !timeoutActiveRef.current) {
            timeoutActiveRef.current = true;
            timeoutRef.current = setTimeout(() => {
                setUpdating(false);
                timeoutActiveRef.current = false;
            }, 2000);
        }
        return () => {
            clearTimeout(timeoutRef.current);
            timeoutActiveRef.current = false;
        };
    }, [updating]);

    useEffect(() => {
        if (!notificationRef.current) return;
        notificationRef.current.style.height = updating ? "50px" : "0px";
        notificationRef.current.style.transition = "height 0.3s ease";
    }, [updating]);

    /* ---------------------------------------------
     * Populate bookingType from query results
     * -------------------------------------------*/
    useEffect(() => {
        const availableBookingTypes = businessBookingTypes?.getBusinessBookingTypes || localBusinessBookingTypes;

        if (!isNewBookingType && availableBookingTypes?.length) {
            const found = availableBookingTypes.find(
                (obj) => Number(obj.id) === Number(id)
            );
            if (found) {
                setBookingType((prev) => (prev?.id === found.id ? prev : found));
                setAdditionalOptions(true);
            }
        } else if (isNewBookingType) {
            // New flow: ensure we don't block on loading UI
            setBookingType({});
            setAdditionalOptions(false);
        }
    }, [businessBookingTypes, id, isNewBookingType, localBusinessBookingTypes]);

    /* ---------------------------------------------
     * Early returns / UI states
     * -------------------------------------------*/
    if (!hasBusinessUrl) {
        if (workspaceLoad.loading) return <div>Loading workspace…</div>;
        if (workspaceLoad.error) return <Alert severity="error" action={
            <Button onClick={() => workspaceLoad.refetch()}>Retry</Button>
        }>Unable to load your workspace. {workspaceLoad.error.message}</Alert>;
        return <Alert severity="info" action={
            <Button onClick={() => navigate("/workspaces")}>Choose workspace</Button>
        }>Choose a workspace to edit its booking types.</Alert>;
    }

    if (shouldFetchBusinessBookingTypes && loading) {
        return <div>Loading…</div>;
    }

    if (shouldFetchBusinessBookingTypes && error) {
        return <div>Error loading booking types.</div>;
    }

    // If updating an existing item, but it doesn't exist — don't hang on "Loading"
    if (!isNewBookingType && !bookingType) {
        return (
            <div style={{ padding: 12 }}>
                Booking Type not found.
                <button style={{ marginLeft: 8 }} onClick={() => navigate("/biz/dashboard")}>
                    Go back
                </button>
            </div>
        );
    }

    /* ---------------------------------------------
     * Handlers
     * -------------------------------------------*/
    const handleBookingBasicOpenDialog = (showPart) => {
        if (isNewBookingType && !hasStandaloneBooking) {
            setCheckPlanNeeded(true);
            return;
        }
        setIsBookingBasicDialogOpen(true);
        if (showPart) setShowBookingBasicDialogSection(showPart);
    };

    const handleBookingBasicCloseDialog = () => setIsBookingBasicDialogOpen(false);

    const handleScheduleSettingsOpenDialog = (showPart) => {
        setIsScheduleSettings(true);
        if (showPart) setShowSection(showPart);
    };

    const handleScheduleSettingsCloseDialog = () => setIsScheduleSettings(false);

    const handleInviteeQuestionCloseDialog = () => setShowInviteeQuestions(false);

    const handleConfirmDelete = () => {
        if (!currentId) return;
        deleteInviteeQuestion({ variables: { id: parseInt(currentId, 10) } });
    };

    const scrollToSection = (ref) => {
        setTimeout(() => {
            const el = document.getElementById(ref);
            if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
        }, 100);
    };

    const getDefinedFields = (obj = {}) =>
        Object.fromEntries(Object.entries(obj).filter(([, value]) => value !== undefined));

    const normalizeAssignedStaffs = (nextAssignedStaffs, fallbackAssignedStaffs = []) => {
        if (!Array.isArray(nextAssignedStaffs)) {
            return Array.isArray(fallbackAssignedStaffs) ? fallbackAssignedStaffs : [];
        }

        const fallbackByStaffId = new Map(
            (Array.isArray(fallbackAssignedStaffs) ? fallbackAssignedStaffs : []).map((staff) => [
                Number(staff?.staffId),
                staff,
            ])
        );

        return nextAssignedStaffs.map((staff) => {
            const fallback = fallbackByStaffId.get(Number(staff?.staffId));
            return {
                ...fallback,
                ...staff,
                staffId: Number(staff?.staffId),
                groupId:
                    staff?.groupId !== undefined && staff?.groupId !== null
                        ? Number(staff.groupId)
                        : fallback?.groupId,
            };
        });
    };

    const handleSubmit = async (submitProps) => {
        if (!bookingType?.id && !hasStandaloneBooking) {
            setUpgradeFeatureName("Booking types");
            setCheckPlanNeeded(true);
            return;
        }
        if (
            !bookingType?.id &&
            !hasAdvancedBookingAccess &&
            existingBookingTypes.length >= FREE_BOOKING_TYPE_LIMIT
        ) {
            setUpgradeFeatureName("More than 2 booking types");
            setCheckPlanNeeded(true);
            return;
        }

        const updatedSubmitProps = {
            ...submitProps,
            businessId: Number(selectedBusiness.businessBasic.id),
        };

        if (bookingType && bookingType.id) {
            // UPDATE
            updateBookingTypeDB({ variables: updatedSubmitProps })
                .then((res) => {
                    const mergedAssignedStaffs = normalizeAssignedStaffs(
                        updatedSubmitProps.assignedStaffs,
                        bookingType?.assignedStaffs
                    );
                    const immediateUpdate = getDefinedFields({
                        title: updatedSubmitProps.title,
                        start: updatedSubmitProps.start,
                        end: updatedSubmitProps.end,
                        recurring: updatedSubmitProps.recurring,
                        recurringInterval: updatedSubmitProps.recurringInterval,
                        calendarSystem: updatedSubmitProps.calendarSystem,
                        scheduleTimezone: updatedSubmitProps.scheduleTimezone,
                        location: updatedSubmitProps.location,
                        meetingLocationTypeId: updatedSubmitProps.meetingLocationTypeId,
                        desc: updatedSubmitProps.desc,
                        color: updatedSubmitProps.color,
                        link: updatedSubmitProps.link,
                        length: updatedSubmitProps.length,
                        maxInvitees: updatedSubmitProps.maxInvitees,
                        price: updatedSubmitProps.price,
                        isPaymentRequired: updatedSubmitProps.isPaymentRequired,
                        showRemaining: updatedSubmitProps.showRemaining,
                        bookingSize: updatedSubmitProps.bookingSize,
                        bookingPerDay: updatedSubmitProps.bookingPerDay,
                        increments: updatedSubmitProps.increments,
                        isVisibleToStaff: updatedSubmitProps.isVisibleToStaff,
                        isPublic: updatedSubmitProps.isPublic,
                        displayOnBookingPage: updatedSubmitProps.displayOnBookingPage,
                        phoneNumber: updatedSubmitProps.phoneNumber,
                        canInviteeCall: updatedSubmitProps.canInviteeCall,
                        image: updatedSubmitProps.image,
                        bookingApproval: updatedSubmitProps.bookingApproval,
                        bookingTypeCategoryId: updatedSubmitProps.bookingTypeCategoryId,
                    });
                    const mergedBookingType = {
                        ...(bookingType || {}),
                        ...immediateUpdate,
                        ...(res?.data?.updateBookingType || {}),
                        assignedStaffs:
                            mergedAssignedStaffs.length > 0
                                ? mergedAssignedStaffs
                                : bookingType?.assignedStaffs || [],
                    };

                    setBookingType(mergedBookingType);

                    setUpdating(true);
                    dispatch(
                        updateBookingType(
                            Number(selectedBusiness.businessBasic.id),
                            mergedBookingType,
                            mergedAssignedStaffs
                        )
                    );
                    refetch();
                    updatedSubmitProps.DialogOpen?.(false);
                })
                .catch((err) => {
                    console.log(err);
                });
        } else {
            // CREATE
            addBookingType({ variables: updatedSubmitProps })
                .then((res) => {
                    const newId = res?.data?.addBookingType?.id;
                    if (newId) {
                        const mergedAssignedStaffs = normalizeAssignedStaffs(
                            submitProps.assignedStaffs,
                            res?.data?.addBookingType?.assignedStaffs
                        );
                        setBookingType({
                            ...res.data.addBookingType,
                            assignedStaffs: mergedAssignedStaffs,
                        });
                        setAdditionalOptions(true);
                        dispatch(
                            updateBookingType(
                                Number(selectedBusiness.businessBasic.id),
                                res.data.addBookingType,
                                mergedAssignedStaffs
                            )
                        );
                        refetch();
                        navigate(`/biz/update/${newId}`);
                    }
                })
                .catch((err) => {
                    console.log(err);
                });
        }
    };

    /* ---------------------------------------------
     * Derived
     * -------------------------------------------*/
    const combinedQuestions = [
        ...(bookingType?.customQuestions || []),
        ...(bookingType?.inviteeQuestions || []),
    ];

    /* ---------------------------------------------
     * Context value
     * -------------------------------------------*/
    const value = {
        props,
        isUpdateMode,
        handleScheduleSettingsOpenDialog,
        additionalOptions,
        selectedBusiness,
        bookingType: bookingType || {},
        errors,
        setErrors,
        setShowInviteeQuestions,
        showInviteeQuestions,
        isBookingBasicDialogOpen,
        handleBookingBasicOpenDialog,
        setIsBookingBasicDialogOpen,
        handleBookingBasicCloseDialog,
        showBookingBasicDialogSection,
        handleInviteeQuestionCloseDialog,
        isDisabled,
        combinedQuestions,
        notificationRef,
        CancelConfirmationModal,
        BookingTypeTime,
        showSection,
        handleSubmit,
        scrollToSection,
        selectedTab,
        providerUser,
        hasStandaloneBooking,
        hasAdvancedBookingAccess,
        updating,
        setUpdating,
        bookingApproval,
        setSelectedTab,
        setIsDisabled,
        isScheduleSettings,
        setIsScheduleSettings,
        ConfirmDialog,
        openDelete,
        setOpenDelete,
        handleConfirmDelete,
        handle: scrollToSection,
        onClose: handleScheduleSettingsCloseDialog,
        refetch,
        getBookingCategory,
    };

    return (
        <>
            {checkPlanNeeded && (
                <UpgradePlan
                    featureName={upgradeFeatureName}
                    requiredPlanId={standardPlanRequired}
                    applyExemption={false}
                    pricingProduct="booking"
                    onClose={() => setCheckPlanNeeded(false)}
                />
            )}
            {!hasStandaloneBooking && (
                <Box sx={{ p: 1.5 }}>
                    <Alert severity="warning">
                        Add Booking first to create booking types. Advanced Booking features require a paid Booking plan.
                    </Alert>
                </Box>
            )}
            <BookingTypeContext.Provider value={value}>
                {children}
            </BookingTypeContext.Provider>
        </>
    );
}

export default BookingTypeProvider;
